import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import {
  Observable,
  map,
  of,
  switchMap,
  take,
  concatMap,
  range,
  reduce,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  VehicleCreateInput,
  VehicleRecord,
  VehicleUpdateInput,
} from '../../models/vehicle';
import { AuthService } from '../auth/auth.service';
import { LoggingService } from '@fleet-monitor/frontend-logging';
import { FleetInvoiceListItem } from '@fleet-monitor/types/invoices';

export interface Fleet {
  _id: string;
  label?: string;
  email?: string;
  /** The signed-in User's permission on this Fleet, as GET /fleet reports it. */
  permissions?: string;
  /** The Fleet's Plan; its tier lists the features it includes. */
  plan?: { tier?: { features?: string[] } };
}

export interface FleetsResponse {
  fleets: Fleet[];
  permissions?: Record<string, string[]>;
}

export interface VehiclesResponse<T = any> {
  vehicles: T[];
  pages?: number;
  count?: number;
}

export interface TicketsResponse<T = any> {
  tickets: T[];
  pages?: number;
  count?: number;
  total?: number;
  unpaid?: number;
  paid?: number;
  oldest?: string;
  payable?: number;
}

export interface FleetResponse {
  fleets: Fleet[];
}
export interface PaymentInfo {
  name?: string;
  expiry?: string;
  last4?: string;
  accountType?: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  phone?: string;
  postal?: string;
  company?: string;
  email?: string;
}
export interface FleetPaymentResponse {
  payment?: PaymentInfo[];
  payments?: PaymentInfo[];
}
export interface FleetInvoicesResponse {
  invoices: FleetInvoiceListItem[];
}

export interface PaymentPlanTier {
  displayLabel: string;
  maxAdmins?: number;
  features?: string[];
}
export interface PaymentPlan {
  _id?: string;
  maxVehicles: number;
  pricePerMonth: number; // in cents
  monthlySearches?: number;
  tier?: PaymentPlanTier;
}

@Injectable({
  providedIn: 'root',
})
export class FleetApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private logger = inject(LoggingService);

  private baseUrl = environment.api.replace(/\/$/, '');

  getFleets(): Observable<FleetsResponse> {
    const url = `${this.baseUrl}/fleet`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) => {
        if (!token) {
          this.logger.warn(
            '[FleetApiService] Missing access token for GET /fleet request'
          );
        }
        return this.http.get<FleetsResponse>(url, {
          headers: this.authHeader(token),
        });
      })
    );
  }

  getFleet(fleetId: string): Observable<FleetResponse> {
    const url = `${this.baseUrl}/fleet/${fleetId}`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get<FleetResponse>(url, { headers: this.authHeader(token) })
      )
    );
  }

  // Onboarding helpers
  addFleet(body: {
    label: string;
    timezone: string;
    active: boolean;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  }): Observable<{ fleet: Fleet }> {
    const url = `${this.baseUrl}/fleet`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post<{ fleet: Fleet }>(url, body, {
          headers: this.authHeader(token),
        })
      )
    );
  }

  uploadVehiclesCsv(
    fleetId: string,
    csvText: string,
    opts?: { defaultState?: string; defaultName?: string }
  ): Observable<{ vehicles: any[]; uncoveredOpenTickets?: number }> {
    let params = new HttpParams();
    if (opts?.defaultState)
      params = params.set('defaultState', opts.defaultState);
    if (opts?.defaultName) params = params.set('defaultName', opts.defaultName);
    const url = `${this.baseUrl}/fleet/${fleetId}/vehicle-upload`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post<{ vehicles: any[]; uncoveredOpenTickets?: number }>(url, csvText, {
          headers: this.authHeader(token),
          params,
        })
      )
    );
  }

  getPaymentPlans(email?: string): Observable<PaymentPlan[]> {
    let params = new HttpParams();
    if (email) params = params.set('email', email);
    const url = `${this.baseUrl}/payment/plans`;
    // Public endpoint; no auth header needed
    return this.http.get<PaymentPlan[]>(url, { params });
  }

  setFleetPlan(
    fleetId: string,
    planId: string,
    billingSchedule: 'monthly' | 'annually' = 'monthly',
    autopayVehicles?: boolean
  ) {
    const url = `${this.baseUrl}/fleet/${fleetId}/plan/${planId}`;
    const body: any = { billingSchedule };
    if (autopayVehicles) body.autopayVehicles = true;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post(url, body, { headers: this.authHeader(token) })
      )
    );
  }

  setEnterprisePlan(
    fleetId: string,
    vehicleCount: number,
    billingSchedule: 'monthly' | 'annually' = 'monthly',
    autopayVehicles?: boolean
  ) {
    const url = `${this.baseUrl}/fleet/${fleetId}/enterpriseplan/${vehicleCount}`;
    const body: any = { billingSchedule };
    if (autopayVehicles) body.autopayVehicles = true;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post(url, body, { headers: this.authHeader(token) })
      )
    );
  }

  getVehicles(
    fleetId: string,
    opts?: {
      page?: number;
      limit?: number;
      sort?: string;
      order?: 'asc' | 'desc';
      query?: string | Record<string, unknown>;
    }
  ): Observable<VehiclesResponse> {
    let params = new HttpParams();
    if (opts?.page != null) params = params.set('page', String(opts.page));
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    if (opts?.sort) params = params.set('sort', opts.sort);
    if (opts?.order) params = params.set('order', opts.order);
    if (opts?.query) {
      const q =
        typeof opts.query === 'string'
          ? opts.query
          : JSON.stringify(opts.query);
      params = params.set('query', q);
    }
    const url = `${this.baseUrl}/fleet/${fleetId}/vehicle`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get<VehiclesResponse>(url, {
          params,
          headers: this.authHeader(token),
        })
      )
    );
  }

  getVehicleCount(
    fleetId: string,
    opts?: { useSearch?: boolean; limit?: number }
  ): Observable<number> {
    if (opts?.useSearch) {
      const limit = opts.limit ?? 250;
      return this.getAllVehicles(fleetId, { limit }).pipe(
        map((vehicles) => vehicles.length)
      );
    }
    return this.getVehicles(fleetId, { page: 0, limit: 1 }).pipe(
      map((resp) => {
        if (resp.count != null) return Number(resp.count);
        if (resp.pages != null) return Number(resp.pages);
        return resp.vehicles?.length || 0;
      })
    );
  }

  getAllVehicles(
    fleetId: string,
    opts?: {
      limit?: number;
      sort?: string;
      order?: 'asc' | 'desc';
      query?: string | Record<string, unknown>;
    }
  ): Observable<any[]> {
    const limit = opts?.limit ?? 250;
    return this.getVehicles(fleetId, {
      page: 0,
      limit,
      sort: opts?.sort,
      order: opts?.order,
      query: opts?.query,
    }).pipe(
      switchMap((first) => {
        const pages = first.pages ?? 1;
        if (pages <= 1) return of(first.vehicles || []);
        // Fetch remaining pages sequentially (not in parallel)
        return range(1, pages - 1).pipe(
          concatMap((p) =>
            this.getVehicles(fleetId, {
              page: p,
              limit,
              sort: opts?.sort,
              order: opts?.order,
              query: opts?.query,
            })
          ),
          map((resp) => resp.vehicles || []),
          reduce((acc, arr) => acc.concat(arr), first.vehicles || [])
        );
      })
    );
  }

  getStatistics(
    fleetId: string
  ): Observable<{
    count: number;
    paid: number;
    unpaid: number;
    total: number;
    payable: number;
    oldest: string;
  }> {
    const url = `${this.baseUrl}/fleet/${fleetId}/ticket/stats`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get<{
          count: number;
          paid: number;
          unpaid: number;
          total: number;
          payable: number;
          oldest: string;
        }>(url, { headers: this.authHeader(token) })
      )
    );
  }

  getTickets(
    fleetId: string,
    opts?: {
      page?: number;
      limit?: number;
      sort?: string;
      order?: 'asc' | 'desc';
      query?: string | Record<string, unknown>;
      location?: string;
      after?: string;
      before?: string;
      state?: string | string[];
      amountMin?: number | null;
      amountMax?: number | null;
    }
  ): Observable<TicketsResponse> {
    this.logger.log(opts?.page);
    let params = new HttpParams();
    if (opts?.page != null) params = params.set('page', String(opts.page));
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    if (opts?.sort) params = params.set('sort', opts.sort);
    if (opts?.order) params = params.set('order', opts.order);
    if (opts?.query) {
      const q =
        typeof opts.query === 'string'
          ? opts.query
          : JSON.stringify(opts.query);
      params = params.set('query', q);
    }
    if (opts?.location) params = params.set('location', opts.location);
    if (opts?.after) params = params.set('after', opts.after);
    if (opts?.before) params = params.set('before', opts.before);
    if (opts?.state) {
      const value = Array.isArray(opts.state)
        ? opts.state.join(',')
        : opts.state;
      if (value) params = params.set('state', value);
    }
    if (opts?.amountMin != null)
      params = params.set('amountMin', String(opts.amountMin));
    if (opts?.amountMax != null)
      params = params.set('amountMax', String(opts.amountMax));
    const url = `${this.baseUrl}/fleet/${fleetId}/ticket`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get<TicketsResponse>(url, {
          params,
          headers: this.authHeader(token),
        })
      )
    );
  }

  /**
   * Get minimal ticket data optimized for dashboard display
   * Excludes revisions, payments, violations, images, and other heavy fields
   * ~98% smaller payload than getTickets()
   */
  getDashboardTickets(
    fleetId: string,
    opts?: {
      limit?: number;
      sort?: string;
      order?: 'asc' | 'desc';
      after?: string;
      before?: string;
    }
  ): Observable<TicketsResponse> {
    let params = new HttpParams();
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));
    if (opts?.sort) params = params.set('sort', opts.sort);
    if (opts?.order) params = params.set('order', opts.order);
    if (opts?.after) params = params.set('after', opts.after);
    if (opts?.before) params = params.set('before', opts.before);
    const url = `${this.baseUrl}/fleet/${fleetId}/ticket/dashboard`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get<TicketsResponse>(url, {
          params,
          headers: this.authHeader(token),
        })
      )
    );
  }

  getAllTickets(
    fleetId: string,
    opts?: {
      limit?: number;
      sort?: string;
      order?: 'asc' | 'desc';
      query?: string | Record<string, unknown>;
      location?: string;
      after?: string;
      before?: string;
      state?: string | string[];
      amountMin?: number | null;
      amountMax?: number | null;
    }
  ): Observable<any[]> {
    const limit = opts?.limit ?? 250;
    return this.getTickets(fleetId, {
      page: 0,
      limit,
      sort: opts?.sort,
      order: opts?.order,
      query: opts?.query,
      location: opts?.location,
      after: opts?.after,
      before: opts?.before,
      state: opts?.state,
      amountMin: opts?.amountMin ?? undefined,
      amountMax: opts?.amountMax ?? undefined,
    }).pipe(
      switchMap((first) => {
        const pages = first.pages ?? 1;
        if (pages <= 1) return of(first.tickets || []);
        // Fetch remaining pages sequentially (not in parallel)
        return range(1, pages - 1).pipe(
          concatMap((p) =>
            this.getTickets(fleetId, {
              page: p,
              limit,
              sort: opts?.sort,
              order: opts?.order,
              query: opts?.query,
              location: opts?.location,
              after: opts?.after,
              before: opts?.before,
              state: opts?.state,
              amountMin: opts?.amountMin ?? undefined,
              amountMax: opts?.amountMax ?? undefined,
            })
          ),
          map((resp) => resp.tickets || []),
          reduce((acc, arr) => acc.concat(arr), first.tickets || [])
        );
      })
    );
  }

  payTickets(
    fleetId: string,
    ticketIds: string[]
  ): Observable<{ success?: boolean }> {
    const url = `${this.baseUrl}/fleet/${fleetId}/pay_tickets`;
    const body = { tickets: ticketIds };
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post<{ success?: boolean }>(url, body, {
          headers: this.authHeader(token),
        })
      )
    );
  }

  private authHeader(token?: string): HttpHeaders | undefined {
    if (!token) {
      return undefined;
    }
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }

  deleteVehicle(fleetId: string, vehicleId: string) {
    const url = `${this.baseUrl}/fleet/${fleetId}/vehicle/${vehicleId}`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.delete(url, { headers: this.authHeader(token) })
      )
    );
  }

  getFleetPayment(fleetId: string): Observable<FleetPaymentResponse> {
    const url = `${this.baseUrl}/fleet/${fleetId}/payment`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get<FleetPaymentResponse>(url, {
          headers: this.authHeader(token),
        })
      )
    );
  }

  getFleetInvoices(
    fleetId: string,
    range?: { start?: string; end?: string }
  ): Observable<FleetInvoicesResponse> {
    const url = `${this.baseUrl}/fleet/${fleetId}/invoices`;
    let params = new HttpParams();
    if (range?.start) params = params.set('start', range.start);
    if (range?.end) params = params.set('end', range.end);
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get<FleetInvoicesResponse>(url, {
          headers: this.authHeader(token),
          params,
        })
      )
    );
  }

  downloadInvoice(
    fleetId: string,
    invoicePath: string
  ): Observable<HttpResponse<Blob>> {
    // `invoicePath` is the stored PDF path (e.g. "<fleetId>/invoice/x.pdf" or a
    // full CDN URL); the assets route wants only the segment after "/invoice/".
    const filePath = invoicePath.split('/invoice/').pop() ?? invoicePath;
    const url = `${this.baseUrl}/fleet/${fleetId}/assets/invoice/${filePath}`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.get(url, {
          headers: this.authHeader(token),
          observe: 'response',
          responseType: 'blob',
        })
      )
    );
  }

  setFleetPayment(
    fleetId: string,
    body: {
      token: string;
      expiry?: string;
      name?: string;
      address?: string;
      city?: string;
      region?: string;
      country?: string;
      phone?: string;
      postal?: string;
      company?: string;
      email?: string;
      defaultacct?: string;
      acctid?: string;
      accttype?: string;
    }
  ) {
    const url = `${this.baseUrl}/fleet/${fleetId}/payment`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post(url, body, { headers: this.authHeader(token) })
      )
    );
  }

  deleteFleetPayment(
    fleetId: string,
    body: { acctid: string; expiry?: string }
  ) {
    const url = `${this.baseUrl}/fleet/${fleetId}/payment`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.request('DELETE', url, {
          body,
          headers: this.authHeader(token),
        })
      )
    );
  }

  getTicketHistoryReport(
    fleetId: string,
    params: {
      start: string;
      end?: string;
      filter?: 'fleet' | 'client' | 'all';
      type?: 'json' | 'csv' | 'excel';
      sort?: string;
    }
  ) {
    const url = `${this.baseUrl}/fleet/${fleetId}/report/history`;
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') httpParams = httpParams.set(k, String(v));
    });
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) => {
        const options: any = {
          params: httpParams,
          headers: this.authHeader(token),
        };
        if (params.type && params.type !== 'json') {
          options.observe = 'response';
          options.responseType = 'blob';
        }
        return this.http.get(url, options);
      })
    );
  }

  addVehicle(
    fleetId: string,
    input: VehicleCreateInput
  ): Observable<{ vehicle: VehicleRecord; uncoveredOpenTickets?: number }> {
    const payload = this.buildVehicleCreatePayload(input);
    const url = `${this.baseUrl}/fleet/${fleetId}/vehicle`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post<{ vehicle: VehicleRecord; uncoveredOpenTickets?: number }>(
          url,
          { vehicle: payload },
          { headers: this.authHeader(token) }
        )
      )
    );
  }

  updateVehicle(
    fleetId: string,
    vehicleId: string,
    update: VehicleUpdateInput
  ): Observable<{ success: boolean; uncoveredOpenTickets?: number }> {
    const url = `${this.baseUrl}/fleet/${fleetId}/vehicle/${vehicleId}`;
    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.put<{ success: boolean; uncoveredOpenTickets?: number }>(
          url,
          { vehicle: update },
          { headers: this.authHeader(token) }
        )
      )
    );
  }

  private buildVehicleCreatePayload(
    input: VehicleCreateInput
  ): Record<string, unknown> {
    const licensePlate = (input.licensePlate || '').trim().toUpperCase();
    const licenseState = (input.licenseState || '').trim().toUpperCase();
    const name = (input.vehicleName || '').trim();
    const ownerName = (input.ownerName || '').trim();

    const payload: Record<string, unknown> = {
      deviceId: '',
      licensePlate,
      licenseState,
      name,
      ownerName,
      autopay: !!input.autopay,
    };

    if (input.vin?.trim()) {
      payload['vin'] = input.vin.trim().toUpperCase();
    }
    if (input.color?.trim()) {
      payload['color'] = { description: input.color.trim() };
    }
    if (input.make?.trim()) {
      payload['make'] = { description: input.make.trim() };
    }
    if (input.model?.trim()) {
      payload['model'] = {
        description: input.model.trim(),
        ...(input.make?.trim() ? { make: input.make.trim() } : {}),
      };
    }

    return payload;
  }

  requestAccess(data: { email: string; message: string }) {
    const url = `${this.baseUrl}/user/request-access`;
    return this.http.post(url, data);
  }

  joinFleet(data: {
    invitationCode: string;
    email: string;
    firstName: string;
    lastName: string;
  }) {
    // For now, this will use the request access endpoint until a dedicated join endpoint is ready.
    return this.requestAccess({
      email: data.email,
      message: `Join request - Name: ${data.firstName} ${data.lastName}, Invitation Code: ${data.invitationCode}`,
    });
  }

  /**
   * Cancel pending payment for tickets
   *
   * @param fleetId The fleet ID
   * @param ticketIds Array of ticket IDs to cancel
   * @returns Observable with cancellation response
   */
  cancelTickets(
    fleetId: string,
    ticketIds: string[]
  ): Observable<{ success?: boolean; message?: string }> {
    const url = `${this.baseUrl}/fleet/${fleetId}/cancel_tickets`;
    const body = { tickets: ticketIds };

    return this.auth.token$.pipe(
      take(1),
      switchMap((token) =>
        this.http.post<{ success?: boolean; message?: string }>(url, body, {
          headers: this.authHeader(token),
        })
      )
    );
  }
}
